export type ExamQuestionPoolDepartment = {
  id: number
  name?: string
}

export type ExamQuestionPoolQuestionItem = {
  id: number
  name?: string
  key?: string
  isTrue: boolean
  order?: number | null
}

export type ExamQuestionPoolQuestion = {
  learningObjectiveId: number
  learningObjectiveName?: string
  learningObjectiveOrder?: number | null
  boardModuleId: number
  boardModuleName?: string
  departmentId?: number | null
  departmentName?: string
  questionBankId: number
  questionName?: string
  difficulty?: number | null
  questionTypeId: number
  questionTypeName?: string
  sharedContentId?: number | null
  sharedContentTitle?: string | null
  sharedContentContent?: string | null
  usageCount?: number
  lastUsedAt?: string | null
  questionItems: ExamQuestionPoolQuestionItem[]
}

export type ExamQuestionPoolLearningObjective = {
  id: number
  name?: string
  learningObjectiveName?: string | null
  order?: number | null
  boardModuleId: number
  boardModuleName?: string
  departmentId?: number | null
  departmentName?: string
  questions: ExamQuestionPoolQuestion[]
}

export type ExamQuestionPoolResponse = {
  learningObjectives: ExamQuestionPoolLearningObjective[]
}

export type ManualExamPageMarginSettings = {
  left: number
  right: number
  top: number
  bottom: number
  coverTop: number
}

export type ManualExamCoverFontSizes = {
  institutionName: number
  facultyName: number
  departmentName: number
  courseName: number
  semesterName: number
  examName: number
  metaInfo: number
  globalRules: number
  instructionsHeader: number
  instructionsBody: number
  additionalNotes: number
}

export type ManualExamContentFontSizes = {
  questionText: number
  optionText: number
  numbering: number
}

export type ManualExamCoverSettings = {
  globalRulesHeader: string
  showLogo: boolean
  showInstitutionName: boolean
  showFacultyName: boolean
  showDepartmentName: boolean
  showCourseName: boolean
  showSemesterName: boolean
  showExamName: boolean
  showBookletCode: boolean
  showExamDuration: boolean
  showQuestionCount: boolean
  showGlobalExamRules: boolean
  showInstructions: boolean
  showAdditionalNotes: boolean
  logoBase64?: string | null
  logoWidth: number
  logoHeight: number
  bookletCodeLabel: string
  durationLabel: string
  questionCountLabel: string
  instructionsHeader: string
  additionalNotes?: string | null
  fontSizes: ManualExamCoverFontSizes
}

export type ManualExamBackCoverSettings = {
  enabled: boolean
  content?: string | null
  fontSize: number
}

export type ManualExamBookletLayoutSettings = {
  pageMargins: ManualExamPageMarginSettings
  cover: ManualExamCoverSettings
  backCover: ManualExamBackCoverSettings
  contentFontSizes: ManualExamContentFontSizes
  printFrontCover: boolean
  insertBlankPageAfterCover: boolean
}

export type QuestionTagSummary = {
  id: number
  name?: string | null
  questionCount?: number | null
}

export type ExamQuestionAssignmentOption = {
  id: number
  name?: string
  key?: string
  isTrue: boolean
  order?: number | null
}

export type ExamQuestionAssignment = {
  examQuestionId: number
  questionBankId: number
  order: number
  point?: number | null
  questionName?: string
  questionTypeId?: number | null
  groupType?: string
  groupId?: number | null
  groupName?: string
  sequenceGroup?: string | null
  sharedContentId?: number | null
  sharedContentTitle?: string | null
  sharedContentContent?: string | null
  learningObjectiveId?: number | null
  learningObjectiveName?: string | null
  optionOrder: number[]
  options: ExamQuestionAssignmentOption[]
  optionOrderByBooklet?: Record<string, number[]>
}

export type ExamQuestionAssignmentGroup = {
  groupType?: string
  groupId?: number | null
  groupName?: string
  questions: ExamQuestionAssignment[]
}

export type ExamQuestionAssignmentsResponse = {
  examId: number
  examName?: string
  examType?: string
  semesterCourseId: number
  questionCount: number
  bookletCodes?: string[]
  bookletQuestionOrder?: Record<string, number[]>
  lastGeneratedAt?: string | null
  hasExistingBooklets?: boolean
  hasBookletAnswers?: boolean
  groups: ExamQuestionAssignmentGroup[]
}

export type ManualExamStatus =
  | 'Preparing'
  | 'SentToMeasurementCenter'
  | 'PrintingInProgress'
  | 'PrintingCompleted'
  | 'ExamCompleted'
  | 'OpticalReadingCompleted'
  | 'GradingCompleted'

export type CreateManualExamPayload = {
  semesterCourseId: number
  examTypeId: number
  contributionPercentage: number
  instructions?: string
  name?: string
  questionCount?: number | null
  durationInMinutes?: number | null
  sharedSemesterCourseIds?: number[]
  supplementaryMaterialPath?: string | null
  supplementaryMaterialFileName?: string | null
}

export type ManualExamCourseShareCourse = {
  semesterCourseId: number
  courseName?: string | null
  courseCode?: string | null
  semesterCourseName?: string | null
  instructorName?: string | null
  organizationId?: number | null
  organizationName?: string | null
  organizationHierarchyName?: string | null
  isPrimary?: boolean | null
  isLocked?: boolean | null
}

export type ManualExamCourseShareInfo = {
  semesterCourseId: number
  selectedCourses: ManualExamCourseShareCourse[]
  availableCourses: ManualExamCourseShareCourse[]
}

export type AddExamQuestionItemPayload = {
  questionBankId: number
  groupType?: string
  groupId: number
  groupName?: string
  sequenceGroup?: string
  sharedContentId?: number | null
  learningObjectiveId?: number | null
  optionOrder?: number[]
}

export type AddExamQuestionsPayload = {
  examId: number
  items: AddExamQuestionItemPayload[]
}

export type ReorderExamQuestionItemPayload = {
  examQuestionId: number
  sequenceGroup?: string | null
}

export type ReorderExamQuestionGroupPayload = {
  groupType?: string
  groupId: number
  groupName?: string
  questions: ReorderExamQuestionItemPayload[]
}

export type ReorderExamQuestionsPayload = {
  examId: number
  groups: ReorderExamQuestionGroupPayload[]
}

export type UpdateOptionOrderPayload = {
  examQuestionId: number
  optionOrder: number[]
  bookletCode?: string
}

export type UpdateManualExamBookletsPayload = {
  examId: number
  bookletCodes: string[]
  layoutSettings?: ManualExamBookletLayoutSettings
  shuffleSharedContentQuestions?: boolean
  sharedContentShuffleStates?: Record<number, boolean>
}

export type GenerateManualExamBookletsPayload = {
  examId: number
  bookletCount?: number
  layoutSettings?: ManualExamBookletLayoutSettings
  shuffleSharedContentQuestions?: boolean
  sharedContentShuffleStates?: Record<number, boolean>
}

export type UpdateExamQuestionPointItemPayload = {
  examQuestionId: number
  point: number
}

export type UpdateExamQuestionPointsPayload = {
  examId: number
  items: UpdateExamQuestionPointItemPayload[]
}

export type ManualExamSettingsResponse = {
  examId: number
  bookletCodes: string[]
  bookletQuestionOrder: Record<string, number[]>
  shuffleSharedContentQuestions?: boolean
  sharedContentShuffleStates?: Record<number, boolean>
  lastGeneratedAt?: string | null
  hasExistingBooklets?: boolean
  hasBookletAnswers?: boolean
  examDurationInMinutes?: number | null
  globalExamRules?: string | null
  status?: ManualExamStatus
  isMeasurementCenterActive?: boolean
  canManageStatus?: boolean
  canSendToMeasurementCenter?: boolean
  canManageBooklets?: boolean
  measurementCenterReturnNote?: string | null
  measurementCenterReturnedAt?: string | null
  measurementCenterReturnedByName?: string | null
  measurementCenterSubmissionNote?: string | null
  measurementCenterReturnHistory?: ManualExamMeasurementCenterHistoryItem[]
  layoutSettings?: ManualExamBookletLayoutSettings
  isDynamicLayoutEnabled?: boolean
  defaultLayoutSettings?: ManualExamBookletLayoutSettings
  restrictionMessage?: string | null
  supplementaryMaterialPath?: string | null
  supplementaryMaterialFileName?: string | null
}

export const createDefaultManualExamLayoutSettings = (): ManualExamBookletLayoutSettings => ({
  pageMargins: {
    left: 18,
    right: 18,
    top: 24,
    bottom: 24,
    coverTop: 20,
  },
  cover: {
    globalRulesHeader: 'Sınav Kuralları',
    showLogo: true,
    showInstitutionName: true,
    showFacultyName: true,
    showDepartmentName: true,
    showCourseName: true,
    showSemesterName: true,
    showExamName: true,
    showBookletCode: true,
    showExamDuration: true,
    showQuestionCount: true,
    showGlobalExamRules: true,
    showInstructions: true,
    showAdditionalNotes: false,
    logoBase64: null,
    logoWidth: 140,
    logoHeight: 90,
    bookletCodeLabel: 'Kitapçık Grubu',
    durationLabel: 'Sınav Süresi',
    questionCountLabel: 'Soru Sayısı',
    instructionsHeader: 'Ek Sınav Talimatları',
    additionalNotes: null,
    fontSizes: {
      institutionName: 16,
      facultyName: 15,
      departmentName: 15,
      courseName: 16,
      semesterName: 14,
      examName: 20,
      metaInfo: 12,
      globalRules: 11,
      instructionsHeader: 13,
      instructionsBody: 11,
      additionalNotes: 11,
    },
  },
  backCover: {
    enabled: false,
    content: null,
    fontSize: 11,
  },
  contentFontSizes: {
    questionText: 12,
    optionText: 11,
    numbering: 12,
  },
  printFrontCover: true,
  insertBlankPageAfterCover: false,
})

const parseBoolean = (value: unknown, fallbackValue: boolean): boolean => {
  if (typeof value === 'boolean') {
    return value
  }

  if (typeof value === 'string') {
    const normalized = value.trim().toLowerCase()
    if (normalized === 'true') {
      return true
    }
    if (normalized === 'false') {
      return false
    }
  }

  if (typeof value === 'number' && Number.isFinite(value)) {
    if (value === 1) {
      return true
    }
    if (value === 0) {
      return false
    }
  }

  return fallbackValue
}

export const normalizeManualExamLayoutSettings = (
  input?: ManualExamBookletLayoutSettings | null
): ManualExamBookletLayoutSettings => {
  const fallback = createDefaultManualExamLayoutSettings()
  if (!input) {
    return fallback
  }

  const pageMargins = input.pageMargins ?? fallback.pageMargins
  const cover = input.cover ?? fallback.cover
  const backCover = input.backCover ?? fallback.backCover
  const contentFontSizes = input.contentFontSizes ?? fallback.contentFontSizes
  const printFrontCover = parseBoolean(input.printFrontCover, fallback.printFrontCover)
  const insertBlankPageAfterCover = parseBoolean(input.insertBlankPageAfterCover, false) && printFrontCover

  const clamp = (value: number, min: number, max: number): number => {
    if (!Number.isFinite(value)) {
      return min
    }
    return Math.min(Math.max(value, min), max)
  }

  const clampFontSize = (value: unknown, fallbackValue: number): number => {
    if (typeof value !== 'number') {
      return fallbackValue
    }
    if (!Number.isFinite(value)) {
      return fallbackValue
    }
    return Math.min(Math.max(value, 8), 48)
  }

  const normalizeMultiline = (value?: string | null): string => {
    if (typeof value !== 'string') {
      return ''
    }

    const replaced = value
      .replace(/\r\n/g, '\n')
      .replace(/\r/g, '\n')
      .replace(/\u2028|\u2029/g, '\n')

    const lines = replaced.split('\n').map((line) => line.trimEnd())
    return lines.join('\n').trim()
  }

  const normalizedLogo =
    typeof cover.logoBase64 === 'string' && cover.logoBase64.trim().length > 0
      ? cover.logoBase64.trim()
      : null
  const normalizedNotes = cover.showAdditionalNotes ? normalizeMultiline(cover.additionalNotes) : ''
  const normalizedBackCoverContent = backCover.enabled ? normalizeMultiline(backCover.content) : ''
  const coverFontSizes = cover.fontSizes ?? fallback.cover.fontSizes

  const normalizedGlobalRulesHeader =
    typeof cover.globalRulesHeader === 'string' && cover.globalRulesHeader.trim().length > 0
      ? cover.globalRulesHeader.trim()
      : fallback.cover.globalRulesHeader

  const normalized: ManualExamBookletLayoutSettings = {
    pageMargins: {
      left: clamp(pageMargins.left ?? fallback.pageMargins.left, 10, 72),
      right: clamp(pageMargins.right ?? fallback.pageMargins.right, 10, 72),
      top: clamp(pageMargins.top ?? fallback.pageMargins.top, 10, 72),
      bottom: clamp(pageMargins.bottom ?? fallback.pageMargins.bottom, 10, 72),
      coverTop: clamp(pageMargins.coverTop ?? fallback.pageMargins.coverTop, 10, 72),
    },
    cover: {
      showLogo: !!cover.showLogo,
      showInstitutionName: !!cover.showInstitutionName,
      showFacultyName: !!cover.showFacultyName,
      showDepartmentName: !!cover.showDepartmentName,
      showCourseName: !!cover.showCourseName,
      showSemesterName: !!cover.showSemesterName,
      showExamName: !!cover.showExamName,
      showBookletCode: !!cover.showBookletCode,
      showExamDuration: !!cover.showExamDuration,
      showQuestionCount: !!cover.showQuestionCount,
      showGlobalExamRules: !!cover.showGlobalExamRules,
      showInstructions: !!cover.showInstructions,
      showAdditionalNotes: !!cover.showAdditionalNotes,
      globalRulesHeader: normalizedGlobalRulesHeader,
      logoBase64: normalizedLogo,
      logoWidth: clamp(
        cover.logoWidth ?? fallback.cover.logoWidth,
        40,
        320
      ),
      logoHeight: clamp(
        cover.logoHeight ?? fallback.cover.logoHeight,
        40,
        240
      ),
      bookletCodeLabel:
        typeof cover.bookletCodeLabel === 'string' && cover.bookletCodeLabel.trim().length > 0
          ? cover.bookletCodeLabel.trim()
          : fallback.cover.bookletCodeLabel,
      durationLabel:
        typeof cover.durationLabel === 'string' && cover.durationLabel.trim().length > 0
          ? cover.durationLabel.trim()
          : fallback.cover.durationLabel,
      questionCountLabel:
        typeof cover.questionCountLabel === 'string' && cover.questionCountLabel.trim().length > 0
          ? cover.questionCountLabel.trim()
          : fallback.cover.questionCountLabel,
      instructionsHeader:
        typeof cover.instructionsHeader === 'string' && cover.instructionsHeader.trim().length > 0
          ? cover.instructionsHeader.trim()
          : fallback.cover.instructionsHeader,
      additionalNotes:
        cover.showAdditionalNotes && normalizedNotes.length > 0 ? normalizedNotes : null,
      fontSizes: {
        institutionName: clampFontSize(
          coverFontSizes?.institutionName,
          fallback.cover.fontSizes.institutionName
        ),
        facultyName: clampFontSize(
          coverFontSizes?.facultyName,
          fallback.cover.fontSizes.facultyName
        ),
        departmentName: clampFontSize(
          coverFontSizes?.departmentName,
          fallback.cover.fontSizes.departmentName
        ),
        courseName: clampFontSize(coverFontSizes?.courseName, fallback.cover.fontSizes.courseName),
        semesterName: clampFontSize(
          coverFontSizes?.semesterName,
          fallback.cover.fontSizes.semesterName
        ),
        examName: clampFontSize(coverFontSizes?.examName, fallback.cover.fontSizes.examName),
        metaInfo: clampFontSize(coverFontSizes?.metaInfo, fallback.cover.fontSizes.metaInfo),
        globalRules: clampFontSize(
          coverFontSizes?.globalRules,
          fallback.cover.fontSizes.globalRules
        ),
        instructionsHeader: clampFontSize(
          coverFontSizes?.instructionsHeader,
          fallback.cover.fontSizes.instructionsHeader
        ),
        instructionsBody: clampFontSize(
          coverFontSizes?.instructionsBody,
          fallback.cover.fontSizes.instructionsBody
        ),
        additionalNotes: clampFontSize(
          coverFontSizes?.additionalNotes,
          fallback.cover.fontSizes.additionalNotes
        ),
      },
    },
    backCover: {
      enabled: !!backCover.enabled,
      content:
        backCover.enabled && normalizedBackCoverContent.length > 0
          ? normalizedBackCoverContent
          : null,
      fontSize: clampFontSize(backCover?.fontSize, fallback.backCover.fontSize),
    },
    contentFontSizes: {
      questionText: clampFontSize(
        contentFontSizes?.questionText,
        fallback.contentFontSizes.questionText
      ),
      optionText: clampFontSize(
        contentFontSizes?.optionText,
        fallback.contentFontSizes.optionText
      ),
      numbering: clampFontSize(
        contentFontSizes?.numbering,
        fallback.contentFontSizes.numbering
      ),
    },
    printFrontCover,
    insertBlankPageAfterCover,
  }

  return normalized
}

export type ManualExamMeasurementCenterHistoryItem = {
  returnedAt?: string | null
  returnedById?: number | null
  returnedByName?: string | null
  note?: string | null
  actionType?: 'Return' | 'StatusChange' | string | null
  status?: ManualExamStatus | null
}

export type ManualExamAnswerKeyImage = {
  contentType?: string | null
  base64Data?: string | null
}

export type ManualExamAnswerKeyOption = {
  letter?: string | null
  text?: string | null
}

export type ManualExamAnswerKeyItem = {
  examQuestionId: number
  questionNumber: number
  letter?: string | null
  questionName?: string | null
  questionImages?: ManualExamAnswerKeyImage[]
  point?: number | null
  options?: ManualExamAnswerKeyOption[]
}

export type ManualExamAnswerKeyResponse = {
  keys: Record<string, ManualExamAnswerKeyItem[]>
  generatedAt?: string | null
}

export type UpdateManualExamStatusPayload = {
  examId: number
  status: ManualExamStatus
  returnNote?: string
  submissionNote?: string
}

export type RemoveExamQuestionResponse = {
  result: string
  bookletsReset?: boolean
  hadBooklets?: boolean
}
